"use client";

import { useState, useRef, useEffect } from "react";

import {
  ChevronLeft,
  ChevronRight,
  Users,
  TrendingUp,
  CheckCircle,
  FileText,
  UserCheck,
  Target,
  AlertTriangle,
  Rocket,
} from "lucide-react";

const HRWhoIsThisFor = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const containerRef = useRef(null);

  const candidateProfiles = [
    {
      profileId: "CANDIDATE_001",
      title: "You're a student aiming for HR/Talent Acquisition",
      description: "Ready to build your HR career foundation",
      status: "ELIGIBLE",
    },
    {
      profileId: "CANDIDATE_002",
      title: "You want off-campus results, not campus dependency",
      description: "Take control of your job search strategy",
      status: "ELIGIBLE",
    },
    {
      profileId: "CANDIDATE_003",
      title: "You want to learn by doing — not by watching 40-slide PPTs",
      description: "Hands-on learning over theoretical knowledge",
      status: "ELIGIBLE",
    },
    {
      profileId: "CANDIDATE_004",
      title: "You're ready to get shortlisted, not just 'apply & pray'",
      description: "Strategic approach to job applications",
      status: "ELIGIBLE",
    },
  ];

  const scrollToIndex = (index) => {
    if (containerRef.current) {
      const cardWidth = containerRef.current.children[0].offsetWidth;
      containerRef.current.scrollTo({
        left: index * cardWidth,
        behavior: "smooth",
      });
      setActiveIndex(index);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const x = e.touches[0].pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    updateActiveIndex();
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    updateActiveIndex();
  };

  const updateActiveIndex = () => {
    if (containerRef.current) {
      const cardWidth = containerRef.current.children[0].offsetWidth;
      const newIndex = Math.round(containerRef.current.scrollLeft / cardWidth);
      setActiveIndex(
        Math.max(0, Math.min(newIndex, candidateProfiles.length - 1))
      );
    }
  };

  const scrollLeftBtn = () => {
    const newIndex = Math.max(0, activeIndex - 1);
    scrollToIndex(newIndex);
  };

  const scrollRightBtn = () => {
    const newIndex = Math.min(candidateProfiles.length - 1, activeIndex + 1);
    scrollToIndex(newIndex);
  };

  const scrollToTop = () => {
    // Modern browsers with smooth scrolling support
    if ("scrollBehavior" in document.documentElement.style) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    } else {
      // Fallback for older browsers - animated scroll
      const scrollStep = -window.scrollY / (500 / 15); // 500ms duration
      const scrollInterval = setInterval(() => {
        if (window.scrollY !== 0) {
          window.scrollBy(0, scrollStep);
        } else {
          clearInterval(scrollInterval);
        }
      }, 15);
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", updateActiveIndex);
      return () => container.removeEventListener("scroll", updateActiveIndex);
    }
  }, []);

  return (
    <div className="bg-black py-20 lg:py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* HR Department Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 backdrop-blur-sm">
              <Target className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-blue-400 tracking-wide uppercase">
                Find if you're the right fit
              </span>
            </div>
          </div>
          <div className="inline-block bg-slate-800/50 rounded-lg px-6 py-3 border border-slate-600/30 mb-6">
            <h2 className="text-3xl md:text-4xl font-bold text-white">
              Is This Fellowship for You?
            </h2>
          </div>
        </div>

        <div className="space-y-10">
          {/* Desktop Grid - Candidate Profile Cards */}
          <div className="hidden md:grid md:grid-cols-2 gap-6">
            {candidateProfiles.map((profile, index) => (
              <div
                key={index}
                className="group relative bg-slate-900/50 border border-slate-600/30 rounded-lg p-6 hover:border-slate-500/50 transition-all duration-300 hover:bg-slate-800/50"
              >
                {/* Profile Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-slate-800/50 rounded-lg p-2 border border-slate-600/30">
                      <Users className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 uppercase">
                        {profile.profileId}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="bg-green-500/10 rounded-full px-2 py-1 text-xs text-green-400 border border-green-500/20 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      {profile.status}
                    </div>
                  </div>
                </div>

                {/* Profile Content */}
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors">
                    {profile.title}
                  </h3>
                  <p className="text-gray-400 text-sm group-hover:text-gray-200 transition-colors">
                    {profile.description}
                  </p>
                </div>

                {/* Assessment Score */}
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden mr-3">
                    <div className="h-full bg-green-400 rounded-full w-full"></div>
                  </div>
                  <span className="text-xs text-green-400 font-medium">
                    MATCH
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Swipeable Carousel - Optimized Padding */}
          <div className="md:hidden relative">
            <div
              ref={containerRef}
              className="flex overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide cursor-grab active:cursor-grabbing"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              {candidateProfiles.map((profile, index) => (
                <div
                  key={index}
                  className="flex-shrink-0 w-full bg-slate-900/50 border border-slate-600/30 rounded-lg p-4 transition-all duration-300 snap-center select-none"
                  style={{ userSelect: "none" }}
                >
                  <div className="flex flex-col items-center text-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="bg-slate-800/50 rounded-lg p-2 border border-slate-600/30">
                        <Users className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-400 uppercase">
                          {profile.profileId}
                        </div>
                      </div>
                    </div>

                    <div className="bg-green-500/10 rounded-full px-3 py-1 text-xs text-green-400 border border-green-500/20 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      {profile.status}
                    </div>

                    <div className="space-y-2">
                      <h3 className="text-base font-bold text-white leading-tight">
                        {profile.title}
                      </h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        {profile.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex justify-center mt-6 gap-6">
            <button
              onClick={scrollLeftBtn}
              disabled={activeIndex === 0}
              className={`p-3 rounded-full border transition-all duration-300 ${
                activeIndex === 0
                  ? "bg-gray-800/50 border-gray-700 text-gray-600 cursor-not-allowed"
                  : "bg-slate-500/10 border-slate-500/30 text-slate-400 hover:bg-slate-500/20 hover:border-slate-400"
              }`}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={scrollRightBtn}
              disabled={activeIndex === candidateProfiles.length - 1}
              className={`p-3 rounded-full border transition-all duration-300 ${
                activeIndex === candidateProfiles.length - 1
                  ? "bg-gray-800/50 border-gray-700 text-gray-600 cursor-not-allowed"
                  : "bg-slate-500/10 border-slate-500/30 text-slate-400 hover:bg-slate-500/20 hover:border-slate-400"
              }`}
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* HR Skills Gap Analysis Section */}
        <div className="group relative overflow-hidden rounded-xl transition-all duration-500 hover:scale-[1.02] mt-8 md:mt-16">
          {/* Professional HR Document Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/10 via-slate-800/20 to-blue-900/10"></div>

          {/* HR Document Pattern */}
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" viewBox="0 0 200 200" fill="none">
              {/* Document grid pattern */}
              <defs>
                <pattern
                  id="grid"
                  width="20"
                  height="20"
                  patternUnits="userSpaceOnUse"
                >
                  <path
                    d="M 20 0 L 0 0 0 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="0.5"
                    className="text-blue-400"
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
          </div>

          <div className="relative p-4 md:p-8 border border-slate-600/30">
            {/* HR Document Header */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-4 md:mb-6 pb-3 md:pb-4 border-b border-slate-600/30">
              <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-0">
                <div className="bg-blue-500/20 p-2 md:p-3 rounded-lg border border-blue-500/30">
                  <FileText className="w-5 h-5 md:w-6 md:h-6 text-blue-400" />
                </div>
                <div>
                  <div className="text-xs text-blue-400 uppercase tracking-wider font-semibold">
                    TALENT ACQUISITION ASSESSMENT
                  </div>
                  <div className="text-base md:text-lg font-bold text-white">
                    Skills Gap Analysis Report
                  </div>
                </div>
              </div>
              <div className="bg-slate-800/50 rounded-lg px-3 md:px-4 py-2 border border-slate-600/30">
                <div className="text-xs text-gray-400">Document ID</div>
                <div className="text-sm font-mono text-white">HR-2024-001</div>
              </div>
            </div>

            {/* Assessment Content */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-4 md:mb-6">
              {/* Current Status */}
              <div className="bg-slate-800/30 rounded-lg p-3 md:p-4 border border-slate-600/30">
                <div className="flex items-center gap-2 mb-2 md:mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <div className="text-xs text-amber-400 uppercase font-semibold">
                    Current Status
                  </div>
                </div>
                <div className="text-sm text-gray-300 mb-1 md:mb-2">
                  Job Search Strategy:
                </div>
                <div className="text-base md:text-lg font-bold text-transparent bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text">
                  "Apply. Pray. Repeat."
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                  <span className="text-xs text-red-400">
                    INEFFECTIVE APPROACH
                  </span>
                </div>
              </div>

              {/* Skills Assessment */}
              <div className="bg-slate-800/30 rounded-lg p-3 md:p-4 border border-slate-600/30">
                <div className="flex items-center gap-2 mb-2 md:mb-3">
                  <Target className="w-4 h-4 text-blue-400" />
                  <div className="text-xs text-blue-400 uppercase font-semibold">
                    Skills Required
                  </div>
                </div>
                <div className="space-y-1 md:space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-300">
                      Strategic Thinking
                    </span>
                    <span className="text-xs text-red-400">GAP</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-300">HR Knowledge</span>
                    <span className="text-xs text-red-400">GAP</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-300">
                      Interview Skills
                    </span>
                    <span className="text-xs text-red-400">GAP</span>
                  </div>
                </div>
              </div>

              {/* Recommended Action */}
              <div className="bg-green-500/10 rounded-lg p-3 md:p-4 border border-green-500/30">
                <div className="flex items-center gap-2 mb-2 md:mb-3">
                  <UserCheck className="w-4 h-4 text-green-400" />
                  <div className="text-xs text-green-400 uppercase font-semibold">
                    Recommendation
                  </div>
                </div>
                <div className="text-sm text-gray-300 mb-1 md:mb-2">
                  Immediate Action:
                </div>
                <div className="text-base md:text-lg font-bold text-green-400 mb-2">
                  Enroll in HR Fellowship
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <span className="text-xs text-green-400">
                    HIGH IMPACT SOLUTION
                  </span>
                </div>
              </div>
            </div>

            {/* Bottom Summary */}
            <div className="bg-slate-900/50 rounded-lg p-3 md:p-4 border border-slate-600/30">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-4">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  <div>
                    <div className="text-sm font-semibold text-white">
                      Professional Development Outcome:
                    </div>
                    <div className="text-xs text-gray-400">
                      Transform your career trajectory with strategic HR
                      training
                    </div>
                  </div>
                </div>
                <div className="bg-green-500/20 rounded-full px-3 md:px-4 py-2 border border-green-500/30 md:ml-auto">
                  <span className="text-xs md:text-sm font-bold text-green-400">
                    CAREER TRANSFORMATION GUARANTEED
                  </span>
                </div>
              </div>
            </div>

            {/* HR Signature Line */}
            <div className="mt-4 md:mt-6 pt-3 md:pt-4 border-t border-slate-600/30 flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-0 text-xs text-gray-500">
              <div>Prepared by: HR Development Team</div>
              <div>Assessment Date: {new Date().toLocaleDateString()}</div>
            </div>
          </div>
        </div>
        <div className="mt-12 text-center">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-black to-blue-950 border border-blue-500/20 p-6 md:p-8 mb-8">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-32 -mt-32" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -ml-32 -mb-32" />

            {/* HR quote background */}
            <div className="absolute inset-0 flex items-center justify-center opacity-5">
              <div className="font-sans text-purple-400/30 text-sm">
                {"People are our greatest asset"}
              </div>
            </div>

            <div className="relative z-10 flex flex-col items-center gap-4">
              <div className="flex flex-col md:flex-row items-center gap-3">
                <Rocket className="w-6 h-6 text-blue-400 animate-bounce" />
                <p className="text-xl md:text-2xl font-medium text-purple-100 text-center md:text-left">
                  Ready to{" "}
                  <span className="font-sans text-green-400">launch</span> your
                  HR career?
                </p>
              </div>
              <div className="px-4 py-2 bg-gray-800/50 rounded-lg font-sans text-sm text-gray-400 border border-gray-700">
                Master In-Demand HR Skills in Just Weeks
              </div>
            </div>
          </div>

          <button
            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white font-bold rounded-lg shadow-lg hover:shadow-blue-600/20 transition-all duration-300 hover:scale-105 relative group"
            onClick={scrollToTop}
          >
            <span className="relative z-10">Apply Now</span>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default HRWhoIsThisFor;
